import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { useMutationWithError, usePaginateQuery } from "@/hooks";
import { api } from "@/lib/api";
import type { Paginate, SearchRequest } from "@/types";

import type { MeetRoom, MeetToken } from "../types";

const include = "participants.user";

export const meetRooms = createQueryKeys("meet-rooms", {
  all: (page?: number, search?: SearchRequest) => [{ page, search }],
  byCode: (code?: string) => [code],
});

export const fetchMeetRoomsQueryOptions = (
  page: number = 0,
  search?: SearchRequest,
) =>
  queryOptions({
    ...meetRooms.all(page, search),
    queryFn: () =>
      api
        .get<
          Paginate<MeetRoom<true, true>>
        >(`/meet/rooms`, { params: { page, include, ...search } })
        .then((r) => r.data),
  });

export const useFetchMeetRooms = (search?: SearchRequest) =>
  usePaginateQuery((page) => fetchMeetRoomsQueryOptions(page, search));

export const fetchMeetRoomQueryOptions = (code?: string, guestToken?: string) =>
  queryOptions({
    ...meetRooms.byCode(code),
    queryFn: () =>
      api
        .get<
          MeetRoom<true, true>
        >(`/meet/rooms/${code}`, { params: { include, guest_token: guestToken } })
        .then((r) => r.data),
    enabled: code !== undefined,
  });

export const useFetchMeetRoom = (code?: string, guestToken?: string) =>
  useQuery(fetchMeetRoomQueryOptions(code, guestToken));

export interface StoreMeetRoomRequest {
  name: string;
  scheduled_at: string | null;
}

export const useCreateMeetRoom = () => {
  const queryClient = useQueryClient();
  return useMutationWithError({
    mutationFn: (data: StoreMeetRoomRequest) =>
      api.post<MeetRoom>("/meet/rooms", data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: meetRooms.all._def });
    },
  });
};

export const useGetRoomToken = () =>
  useMutation({
    mutationFn: (code: string) =>
      api.post<MeetToken>(`/meet/rooms/${code}/token`).then((r) => r.data),
  });

export const useAcceptJoinRequest = (roomCode: string) => {
  const queryClient = useQueryClient();
  return useMutationWithError({
    mutationFn: (participantId: number) =>
      api
        .post(`/meet/rooms/${roomCode}/participants/${participantId}/accept`)
        .then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries(meetRooms.byCode(roomCode));
    },
  });
};

export interface GuestJoinRequest {
  name?: string;
  email?: string;
  guest_token?: string;
}

export const useGetGuestRoomToken = (roomCode: string) =>
  useMutation({
    mutationFn: (data: GuestJoinRequest) =>
      api
        .post<MeetToken>(`/meet/rooms/${roomCode}/guest-token`, data)
        .then((r) => r.data),
  });

export const useAcceptAllJoinRequests = (roomCode: string) => {
  const queryClient = useQueryClient();
  return useMutationWithError({
    mutationFn: () =>
      api
        .post(`/meet/rooms/${roomCode}/participants/accept-all`)
        .then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries(meetRooms.byCode(roomCode));
    },
  });
};
